/* ── Spike chart ─────────────────────────────────────────────────────
   Tapered-bar chart built on recharts, with custom SVG spike shapes,
   gradient fills, glow effects, and straight (never rotated) labels.
   Also includes the summary stat row that sits beneath every spike chart. */

import { useLayoutEffect, useRef } from "react";
import { BarChart3, TrendingDown } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  type ChartDatum,
  chartTotal,
  chartRanked,
  SPIKE_SLOT_WIDTH,
  SPIKE_LABEL_CHAR,
  SPIKE_LABEL_GUTTER,
} from "../constants/pagInsightsConstants";
import { wrapCategoryLabel } from "../helpers/pagHelpers";

/* ── Gradient / glow id helpers ──────────────────────────────────── */

const spikeGradientId = (prefix: string, index: number) => `${prefix}-grad-${index}`;
const spikeGlowId = (prefix: string) => `${prefix}-glow`;

/* ── Spike bar shape ─────────────────────────────────────────────── */

/** Smooth tapered spike path used as the <Bar> shape. */
const getSpikePath = (x: number, y: number, width: number, height: number) =>
  `M${x},${y + height}
   C${x + width / 3},${y + height} ${x + width / 2},${y + height / 3} ${x + width / 2},${y}
   C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${x + width},${y + height}
   Z`;

type SpikeBarProps = {
  data?: ChartDatum[];
  prefix?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
};

const SpikeBar = ({
  data = [],
  prefix = "spike",
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  index = 0,
}: SpikeBarProps) => {
  const color = data[index]?.color ?? "#8b949e";
  const cx = x + width / 2;
  return (
    <g>
      <path d={getSpikePath(x, y, width, height)} stroke="none" fill={`url(#${spikeGradientId(prefix, index)})`} />
      <circle cx={cx} cy={y} r={7} fill={color} opacity={0.35} filter={`url(#${spikeGlowId(prefix)})`} />
      <circle cx={cx} cy={y} r={4} fill={color} />
      <circle cx={cx} cy={y} r={2} fill="#ffffff" />
    </g>
  );
};

/* ── Value label ─────────────────────────────────────────────────── */

type SpikeValueLabelProps = {
  data?: ChartDatum[];
  x?: number;
  y?: number;
  width?: number;
  value?: number;
  index?: number;
};

const SpikeValueLabel = ({ data = [], x = 0, y = 0, width = 0, value, index = 0 }: SpikeValueLabelProps) => (
  <text
    x={x + width / 2}
    y={y - 14}
    textAnchor="middle"
    fontSize={13}
    fontWeight={700}
    fill={data[index]?.color ?? "#8b949e"}
  >
    {value}
  </text>
);

/* ── Tick label ──────────────────────────────────────────────────── */

type SpikeTickProps = {
  data?: ChartDatum[];
  variant?: "badge" | "percent";
  total?: number;
  x?: number;
  y?: number;
  payload?: { value?: string; index?: number };
};

export const SpikeTick = ({
  data = [],
  variant = "badge",
  total = 0,
  x = 0,
  y = 0,
  payload,
}: SpikeTickProps) => {
  const index = payload?.index ?? 0;
  const item = data.find((d) => d.name === payload?.value) ?? data[index];
  const color = item?.color ?? "#8b949e";
  const lines = wrapCategoryLabel(payload?.value ?? "");
  const percent = total > 0 ? ((item?.value ?? 0) / total) * 100 : null;

  if (variant === "percent") {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <title>{payload?.value}</title>
        {lines.map((line, i) => (
          <text
            key={`${line}-${i}`}
            x={0}
            y={16 + i * 13}
            dy={4}
            textAnchor="middle"
            fontSize={11}
            fontWeight={600}
            fill={color}
          >
            {line}
          </text>
        ))}
        {percent !== null && (
          <text x={0} y={16 + lines.length * 13} dy={5} textAnchor="middle" fontSize={10} fill="#8b949e">
            {percent.toFixed(2)}%
          </text>
        )}
      </g>
    );
  }

  return (
    <g transform={`translate(${x}, ${y})`}>
      <title>{payload?.value}</title>
      {lines.map((line, i) => (
        <text
          key={`${line}-${i}`}
          x={0}
          y={16 + i * 13}
          dy={4}
          textAnchor="middle"
          fontSize={11}
          fontWeight={600}
          fill={color}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

/* ── Tooltip ─────────────────────────────────────────────────────── */

type ChartTooltipProps = {
  active?: boolean;
  total?: number;
  payload?: Array<{ payload?: ChartDatum }>;
  coordinate?: { x: number; y: number };
  containerRef?: React.RefObject<HTMLDivElement | null>;
};

export const ChartTooltip = ({
  active,
  total = 0,
  payload,
  coordinate,
  containerRef,
}: ChartTooltipProps) => {
  const row = payload?.[0]?.payload;
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = tooltipRef.current;
    const container = containerRef?.current;
    if (!active || !el || !container) return;

    const adjustPosition = () => {
      el.style.transform = "";

      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const padding = 12;
      const flipOffset = elRect.width + 20;

      if (elRect.right > containerRect.right - padding) {
        const flippedLeft = elRect.left - flipOffset;
        if (flippedLeft >= containerRect.left + padding) {
          el.style.transform = `translateX(-${flipOffset}px)`;
        } else {
          const overflow = elRect.right - (containerRect.right - padding);
          el.style.transform = `translateX(-${overflow}px)`;
        }
      } else if (elRect.left < containerRect.left + padding) {
        const flippedRight = elRect.right + flipOffset;
        if (flippedRight <= containerRect.right - padding) {
          el.style.transform = `translateX(${flipOffset}px)`;
        } else {
          const underflow = containerRect.left + padding - elRect.left;
          el.style.transform = `translateX(${underflow}px)`;
        }
      }
    };

    adjustPosition();

    container.addEventListener("scroll", adjustPosition, { passive: true });
    window.addEventListener("resize", adjustPosition, { passive: true });

    return () => {
      container.removeEventListener("scroll", adjustPosition);
      window.removeEventListener("resize", adjustPosition);
    };
  }, [active, payload, coordinate, containerRef]);

  if (!active || !row) return null;
  const percent = total > 0 ? ((row.value ?? 0) / total) * 100 : null;
  return (
    <div
      ref={tooltipRef}
      className="pointer-events-none rounded-lg border border-border bg-card/95 px-3 py-2 shadow-lg backdrop-blur-sm"
    >
      <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
        <span className="size-2 rounded-full" style={{ background: row.color ?? "#818cf8" }} />
        {row.name}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">
        <span className="font-semibold text-foreground">{row.value}</span> Accounts
      </div>
      {percent !== null && (
        <div className="text-[11px] font-semibold" style={{ color: row.color ?? "#818cf8" }}>
          {percent.toFixed(2)}%
        </div>
      )}
    </div>
  );
};

/* ── Empty state ─────────────────────────────────────────────────── */

export const ChartEmptyState = () => (
  <div className="flex h-[290px] flex-col items-center justify-center gap-1 text-center">
    <BarChart3 className="size-6 text-muted-foreground/60" />
    <p className="text-sm font-medium text-foreground">No data to display</p>
    <p className="text-[11px] text-muted-foreground">
      No data is available.
    </p>
  </div>
);

/* ── Slot width ──────────────────────────────────────────────────── */

/* wrapCategoryLabel only breaks on spaces, so a single long token
   ("Administrators", "cmts_group4") stays one line and overruns a fixed
   slot into its neighbour. Every slot is therefore as wide as the widest
   label the set actually produces; the plot scrolls past that, as before. */
const spikeSlotWidth = (data: ChartDatum[]) =>
  data.reduce((widest, entry) => {
    const longest = wrapCategoryLabel(entry.name).reduce(
      (max, line) => Math.max(max, line.length),
      0,
    );
    return Math.max(widest, longest * SPIKE_LABEL_CHAR + SPIKE_LABEL_GUTTER);
  }, SPIKE_SLOT_WIDTH);

/* ── Click helper ────────────────────────────────────────────────── */

/* The bar's own shape is a custom <g> that does not take recharts' event
   props, so the click is read off the chart's active payload instead - which
   also means the whole column, not just the spike, is the target. */
export const spikeClickDatum = (state: unknown): ChartDatum | undefined =>
  (state as { activePayload?: { payload?: ChartDatum }[] } | null)?.activePayload?.[0]?.payload;

/* ── Tick formatter ──────────────────────────────────────────────── */

export const formatYAxisTick = (val: number): string => {
  if (val >= 1_000_000) {
    const formatted = (val / 1_000_000).toFixed(val % 1_000_000 === 0 ? 0 : 1);
    return `${formatted}M`;
  }
  if (val >= 1_000) {
    const formatted = (val / 1_000).toFixed(val % 1_000 === 0 ? 0 : 1);
    return `${formatted}k`;
  }
  return String(val);
};

/* ── SpikeChart ──────────────────────────────────────────────────── */

export const SpikeChart = ({
  data,
  prefix,
  tickVariant = "badge",
  className = "h-[290px] w-full",
  onSelect,
}: {
  data: ChartDatum[];
  prefix: string;
  tickVariant?: "badge" | "percent";
  className?: string;
  onSelect?: (datum: ChartDatum) => void;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const total = chartTotal(data);
  const slotWidth = spikeSlotWidth(data);
  const isScrollable = data.length > 5 || data.length * slotWidth > 380;

  return (
    <div
      ref={scrollContainerRef}
      className={`${className} min-w-0 w-full overflow-x-auto overflow-y-hidden`}
    >
      {/* Each category needs room for a straight, unrotated label. Past that
          the plot scrolls sideways instead of squeezing the names together. */}
      <div
        className="h-full w-full"
        style={isScrollable ? { minWidth: data.length * slotWidth } : undefined}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 32, right: 20, left: 6, bottom: 0 }}
            className={onSelect ? "cursor-pointer" : undefined}
            onClick={
              onSelect
                ? (state: unknown) => {
                    const datum = spikeClickDatum(state);
                    if (datum) onSelect(datum);
                  }
                : undefined
            }
          >
            <defs>
              <filter id={spikeGlowId(prefix)} x="-150%" y="-150%" width="400%" height="400%">
                <feGaussianBlur stdDeviation="3.5" />
              </filter>
              {data.map((entry, index) => (
                <linearGradient key={entry.name} id={spikeGradientId(prefix, index)} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={entry.color} stopOpacity={0.95} />
                  <stop offset="55%" stopColor={entry.color} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={entry.color} stopOpacity={0.12} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="2 6" stroke="#2d3748" vertical={false} />
            <XAxis
              dataKey="name"
              interval={0}
              height={tickVariant === "badge" ? 50 : 62}
              tickLine={false}
              axisLine={{ stroke: "#2d3748" }}
              tick={<SpikeTick data={data} variant={tickVariant} total={total} />}
            />
            <YAxis
              allowDecimals={false}
              width={46}
              tickLine={false}
              axisLine={false}
              tick={{ fill: "#8b949e", fontSize: 10 }}
              tickFormatter={formatYAxisTick}
              domain={[0, (dataMax: number) => Math.max(Number.isFinite(dataMax) ? dataMax : 0, 5)]}
            />
            <Tooltip
              cursor={{ fill: "rgba(139,148,158,0.06)" }}
              isAnimationActive={false}
              content={<ChartTooltip total={total} containerRef={scrollContainerRef} />}
            />
            <Bar
              dataKey="value"
              shape={<SpikeBar data={data} prefix={prefix} />}
              label={<SpikeValueLabel data={data} />}
              isAnimationActive={false}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ── Chart summary stat ──────────────────────────────────────────── */

const ChartSummaryStat = ({
  icon: Icon,
  label,
  item,
}: {
  icon: typeof BarChart3;
  label: string;
  item?: ChartDatum;
}) => (
  <div className="flex min-w-0 flex-1 items-center gap-2.5">
    <span
      className="flex size-8 shrink-0 items-center justify-center rounded-lg"
      style={{ background: `${item?.color ?? "#8b949e"}1f` }}
    >
      <Icon className="size-4" style={{ color: item?.color ?? "#8b949e" }} />
    </span>
    <div className="min-w-0">
      <div className="text-[10px] font-medium tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs font-semibold" style={{ color: item?.color ?? "#8b949e" }}>
        {item ? `${item.name} (${item.value})` : "-"}
      </div>
    </div>
  </div>
);

export const ChartSummary = ({
  data,
  highestLabel,
  lowestLabel,
}: {
  data: ChartDatum[];
  highestLabel: string;
  lowestLabel: string;
}) => {
  const ranked = chartRanked(data);
  return (
    <div className="mt-3 flex items-center gap-3 rounded-lg border border-border bg-background/40 px-3 py-2.5">
      <ChartSummaryStat icon={BarChart3} label={highestLabel} item={ranked[0]} />
      <div className="h-8 w-px shrink-0 bg-border" />
      <ChartSummaryStat icon={TrendingDown} label={lowestLabel} item={ranked[ranked.length - 1]} />
    </div>
  );
};
